import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from './EvaluationsView.module.css';
import { config } from "../../app/utils/config";
import { checkPermission, showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import {
    getEvaluationsReport,
    setEvaluationAddModal,
    setOnEvaluateCollab,
    setOnEvaluateDate,
    setOnEvaluateTeamId,
    setAddResponse,
    setEditResponse,
    setSelectedType,
    setAllowedToEdit,
} from "../../components/evaluations/evaluationsSlice";
import moment from "moment";
import Langs from "../../app/lang/langs";
import EvaluationsToolbar from "./components/EvaluationsToolbar";
import EvaluationsTable from "./components/EvaluationsTable";
import {
    getAllowedTeamIds,
    getTeamMonthData,
    convertReportsToArray,
    processReports,
    summarizeReports
} from "./utils/evaluationUtils";

const MANAGER_TYPE_ID = 4;

export default function EvaluationsViewManagers({ date, onDateChange }) {

    const dispatch = useDispatch();

    const selectedTeams = useSelector((state) => state.teams.selectedTeamsIds);
    const reports = useSelector((state) => state.evaluations.report);
    const myTeam = useSelector((state) => state.teams.myTeam);
    const addEvaluationRes = useSelector((state) => state.evaluations.addResponse);
    const editEvaluationRes = useSelector((state) => state.evaluations.editResponse);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const [expandedTeamId, setExpandedTeamId] = useState(null);
    const [onViewTeam, setOnViewTeam] = useState(null);

    // ─── Derived values ───────────────────────────────────────────────────────

    const year = useMemo(() => date.getFullYear(), [date]);

    const allowedTeams = useMemo(
        () => (selectedTeams?.length ? getAllowedTeamIds(selectedTeams) : []),
        [selectedTeams]
    );

    const groupedReports = useMemo(
        () => convertReportsToArray(reports, year),
        [reports, year]
    );

    const processedReports = useMemo(
        () => processReports(groupedReports, year),
        [groupedReports, year]
    );

    const summarizedReport = useMemo(
        () => summarizeReports(processedReports),
        [processedReports]
    );

    const visibleTeams = useMemo(() =>
        allowedTeams.filter(id =>
            !myTeam ||
            checkPermission(config.scopes.evaluations, config.permissionTypes.view, myTeam.id) ||
            myTeam.id !== id
        ),
        [allowedTeams, myTeam]
    );

    const monthColumns = useMemo(() =>
        Array.from({ length: 12 }, (_, index) => ({
            key: index + 1,
            header: (
                <div>
                    <Langs str={new Date(2000, index).toLocaleString('default', { month: 'short' })} />
                    <br />
                    {getTeamMonthData(summarizedReport, onViewTeam, index + 1)}
                </div>
            ),
        })),
        [summarizedReport, onViewTeam]
    );

    const groupedData = useMemo(() => {
        const groups = {};
        groupedReports.forEach(row => {
            if (!groups[row.id_team]) {
                groups[row.id_team] = { team_name: row.team_name, id_team: row.id_team, rows: [] };
            }
            groups[row.id_team].rows.push(row);
        });
        return Object.values(groups);
    }, [groupedReports]);

    // ─── Effects ─────────────────────────────────────────────────────────────

    useEffect(() => {
        if (year && allowedTeams.length) {
            dispatch(getEvaluationsReport({
                idTeams: allowedTeams,
                year,
                idType: MANAGER_TYPE_ID,
            }));
        }
    }, [allowedTeams, year]);

    useEffect(() => {
        if (selectedTeams.length === 1) {
            setOnViewTeam(selectedTeams[0]);
        } else if (selectedTeams.length > 1) {
            setOnViewTeam(null);
        }
    }, [selectedTeams]);

    // ─── Response handlers ────────────────────────────────────────────────────

    const handleApiResponse = useCallback((res, clearAction) => {
        const lower = res.trim().toLowerCase();
        if (lower.includes("successfully")) {
            showSuccessToast("Success!");
        } else if (lower.includes("duplicate")) {
            showErrorToast(<Langs str="DUPICATE_WARNING" />);
        } else {
            showErrorToast(res);
        }
        dispatch(clearAction(null));
    }, [dispatch]);

    useEffect(() => {
        if (addEvaluationRes != null) handleApiResponse(addEvaluationRes, setAddResponse);
    }, [addEvaluationRes]);

    useEffect(() => {
        if (editEvaluationRes != null) handleApiResponse(editEvaluationRes, setEditResponse);
    }, [editEvaluationRes]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const toggleGroupExpansion = useCallback((teamId) => {
        setExpandedTeamId(prev => {
            const next = prev === teamId ? null : teamId;
            setOnViewTeam(next);
            return next;
        });
    }, []);

    const showModalEditAdd = useCallback((collab, date, entreeDate, sortieDate, id_team, dateStartMonth) => {
        const isAllowed =
            (entreeDate ? date.isAfter(entreeDate) : true) &&
            (sortieDate ? dateStartMonth.isBefore(moment(sortieDate)) : true);
        dispatch(setAllowedToEdit(isAllowed));
        dispatch(setOnEvaluateTeamId(id_team));
        dispatch(setOnEvaluateCollab(collab));
        dispatch(setOnEvaluateDate(date));
        dispatch(setEvaluationAddModal(true));
        dispatch(setSelectedType({ id: MANAGER_TYPE_ID }));
    }, [dispatch]);

    // ─── Render ───────────────────────────────────────────────────────────────

    const scrollHeight = isMobile === 1
        ? 'calc(100vh - 250px)'
        : isMobile === 3 ? 'calc(100vh - 165px)' : 'calc(100vh - 350px)';

    return (
        <div className={`${styles.collabsCard} ${isMobile === 2 ? styles.mobileBody : ''}`}>
            <EvaluationsToolbar
                date={date}
                onDateChange={(val) => onDateChange(val)}
                isMobile={isMobile}
                showTypeSelector={false}
            />
            <EvaluationsTable
                scrollHeight={scrollHeight}
                groupedData={groupedData}
                monthColumns={monthColumns}
                visibleTeams={visibleTeams}
                expandedTeamId={expandedTeamId}
                onToggleGroup={toggleGroupExpansion}
                year={year}
                onClickCell={showModalEditAdd}
            />
        </div>
    );
}
